import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useGridFilterStore } from 'src/store/useGridFilters/useGridFilterStore';
import { goToZara } from 'src/utils/init/configuration';
import { getCategoryUrl, getSubCategoryUrl, getURLFragmentId } from 'src/utils/seo-urls';
import useUniverseQuery from '../React_Query/Search/Universe/useUniverseQuery';
import {  CustomPageUniverseDto } from './universe';
import { GridFilters } from 'src/store/useGridFilters/gridTypes';
import { AxiosResponse } from 'axios';
import { useNavigationHistory } from 'src/utils/providers/NavigationHistoryProvider';
import { gridIdForZara } from 'src/api/services/search/searchApi';
import { useShop } from './Shop/useShop';
import { getPathFragment } from 'src/utils/BasePathTools';

interface GridContext {
  universe: any;
  grid?: any;
  subGrid?: any;
  parentGrid?: any;
}
const findUniverseById = (universes: any[], id: number) => 
  universes?.find(univ => univ.id === id);

export const findGridById = (grids: any[], id: number) => 
  grids?.find(grid => grid.id === id);

const findSubGridById = (subGrids: any[], id: number) => 
  subGrids?.find(subGrid => subGrid.id === id);

const buildGridContext = (universeResponse: any, urlIds: any): GridContext | null => {
  const universe = findUniverseById(universeResponse?.data?.content, urlIds.universeId);
  if (!universe) return null;

  if (urlIds.parentGridId) {
    // SubGrid case
    const parentGrid = findGridById(universe?.grids, urlIds.parentGridId);
    const subGrid = findSubGridById(parentGrid?.subGrids, urlIds.gridReferenceId);
    return { universe, parentGrid, subGrid };
  } else {
    // Main Grid case
    const grid = findGridById(universe?.grids, urlIds.gridReferenceId);
    return { universe, grid };
  }
};

const handleInitialStateWithUrlIds = (
  urlIds: any,
  universeResponse: any,
  SET_FILTER: any,
  setDefaultUniverseAndGrid: () => void,
  history: any
) => {
  const context = buildGridContext(universeResponse, urlIds);
  
if (!context || 
    !context.universe || 
    (!context.grid && !context.subGrid) ||
    (context.subGrid && !context.parentGrid) ||
    (urlIds.parentGridId && !context.subGrid) ||
    (!urlIds.parentGridId && !context.grid)) {
    setDefaultUniverseAndGrid();
    return;
  }

  const filterIds = {
    universeId: urlIds.universeId,
    gridReferenceId: urlIds.gridReferenceId,
    ...(urlIds.parentGridId && { parentGridId: urlIds.parentGridId }),
  };

  SET_FILTER("gridReferenceId", urlIds.gridReferenceId, filterIds);

  if (context.subGrid && context.parentGrid) {
    const targetUrl = getSubCategoryUrl(context.universe, context.parentGrid, context.subGrid);
    history.push(targetUrl);
  } else if (context.grid) {
    const targetUrl = getCategoryUrl(context.universe, context.grid);
    history.push(targetUrl);
  }
};

const handleMainGridNavigation = (
  gridFilters: any,
  universeResponse: any,
  history: any,
  setDefaultUniverseAndGrid: any,
  getCategoryUrl: any
) => {
  const universe = findUniverseById(universeResponse?.data?.content, gridFilters.universeId);
  if (!universe) {
    setDefaultUniverseAndGrid();
    return;
  }

  const grid = findGridById(universe.grids, gridFilters.gridReferenceId);
  if (!grid) {
    setDefaultUniverseAndGrid();
    return;
  }

  const targetUrl = getCategoryUrl(universe, grid);
  const currentPath = history.location.pathname;
 if (currentPath === targetUrl || currentPath.includes(targetUrl)) {
    return;
  }

  history.push(targetUrl);
};

const handleSubGridNavigation = (
  gridFilters: GridFilters,
  universeResponse: AxiosResponse<CustomPageUniverseDto, any>,
  history: any,
  setDefaultUniverseAndGrid: () => void,
  getSubCategoryUrl: any
) => {
  const universe = findUniverseById(universeResponse?.data?.content, gridFilters.universeId);
  if (!universe) {
    setDefaultUniverseAndGrid();
    return;
  }

  const parentGrid = findGridById(universe.grids, gridFilters.parentGridId);
  const subGrid = findSubGridById(parentGrid?.subGrids, gridFilters.gridReferenceId);

  if (!subGrid || !parentGrid) {
    setDefaultUniverseAndGrid();
    return;
  }

  const targetUrl = getSubCategoryUrl(universe, parentGrid, subGrid);
  history.push(targetUrl);
};

export const getUrlIds = (url: string) => {
  const urlSegments = url.split('/');
  const productsPath = urlSegments.findIndex(segment => segment === 'products');
  if (productsPath === -1) return undefined;
  if (urlSegments[productsPath + 2] !== undefined) {
    const universeId = getURLFragmentId(decodeURIComponent(urlSegments[productsPath + 1]))
    const gridId = getURLFragmentId(decodeURIComponent(urlSegments[productsPath + 2]))    
    const subGridId = urlSegments[productsPath + 3] ? getURLFragmentId(decodeURIComponent(urlSegments[productsPath + 3])) : null
    
    const gridReferenceId = subGridId ? Number(subGridId) : Number(gridId);
    const parentGridId = subGridId ? Number(gridId) : null;
    
    return { 
      universeId: Number(universeId), 
      gridReferenceId,
      parentGridId
    }
  } else {
    return undefined
  }
}

export default function useGridMainLogic() {
  const history = useHistory();
  const { shopMarketId, shopLang } = useShop()
  const SET_FILTER = useGridFilterStore((state) => state.SET_MAIN_FILTER);
  const SET_DEFAUlT_URL = useGridFilterStore((state) => state.SET_DEFAUlT_URL);
  const gridFilters = useGridFilterStore((state) => state.gridMainFilters);
  const { data: universeResponse, isSuccess: isUniverseResponseSuccess } =
    useUniverseQuery();

  const getDefaultGridAndUniverse = () => {
    const universe =
      universeResponse?.data?.content?.length > 0 &&
      universeResponse?.data?.content[0];
    const grid = universe?.grids?.length > 0 && universe?.grids[0];

    if (!!universe && !!grid) {
      return { grid, universe };
    } else {
      return null;
    }
  };

  const getDefaultGridURL = async () => {
    if (getDefaultGridAndUniverse()) {
      const parcialUrlToRedirect = await gridIdForZara({
        storeId: String(shopMarketId),
        universeCode: "woman",
        gridCode: "default", 
        lang: shopLang,
      });
      const defaultUrlForRedirect = getPathFragment(parcialUrlToRedirect);
      SET_DEFAUlT_URL("defaultGridUrl", defaultUrlForRedirect);
    } else {
      return null;
    }
  };

  const setDefaultUniverseAndGrid = () => {
    if (getDefaultGridAndUniverse()) {
      const { grid, universe } = getDefaultGridAndUniverse();
      SET_FILTER("defaultGridUrl", grid.id, {
        gridReferenceId: grid.id,
        universeId: universe.id,
        parentGridId: null,
      });
      const targetUrl = getCategoryUrl(universe, grid);
      history.push(targetUrl);
    } else {
      goToZara();
    }
  };

useEffect(() => {
  if (!isUniverseResponseSuccess) return;

  if (gridFilters.isInitialState) {
    // Handle first load of the products page via URL
    getDefaultGridURL();
    
    const urlIds = getUrlIds(window.location.href);
    
    if (urlIds) {
      handleInitialStateWithUrlIds(
        urlIds, 
        universeResponse, 
        SET_FILTER, 
        setDefaultUniverseAndGrid,
        history
      );
    } else if (universeResponse?.data?.content?.length > 0) {
      setDefaultUniverseAndGrid();
    } else {
      goToZara();
    }
  } else {
    // Handle navigation with existing filters
    if (gridFilters.gridReferenceId && gridFilters.universeId) {
      if (!gridFilters.parentGridId) {
        // Main Grid
        handleMainGridNavigation(
          gridFilters,
          universeResponse,
          history,
          setDefaultUniverseAndGrid,
          getCategoryUrl
        );
      } else {
        // Sub Grid
        handleSubGridNavigation(
          gridFilters,
          universeResponse,
          history,
          setDefaultUniverseAndGrid,
          getSubCategoryUrl
        );
      }
    }
  }
}, [isUniverseResponseSuccess])
}
